import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  EmptyState,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { getOrgSupplierDetail } from '../lib/api';

/**
 * /o/:orgSlug/suppliers/:id — Org-tier supplier detail.
 *
 * V3-2-10: Supplier header, undisclosed-AI banner, scorecard with
 * stacked bar, disclosure versions, request timeline, drift alerts.
 * Design reference:
 * .claude/skills/ux-adversary/runs/v3-2-10-portfolio/round-2/supplier-detail.html
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ScoreBand = 'red' | 'amber' | 'green';

interface ScorecardField {
  field: string;
  score_band: ScoreBand;
  score_value: number;
  score_max: number;
  citation_ref: string;
  citation_summary: string;
  citation_detail: string;
}

interface DisclosureVersion {
  version: string;
  submitted_at: string;
  source: string;
  content_hash: string | null;
}

interface TimelineStep {
  label: string;
  occurred_at: string | null;
  status: 'done' | 'active' | 'pending';
}

interface DriftAlert {
  field: string;
  old_band: ScoreBand;
  new_band: ScoreBand;
  reason: string;
  detected_at: string;
}

interface UndisclosedAiDetail {
  tools: string[];
  description: string;
}

interface SupplierDetailData {
  portfolio_entry_id: string;
  supplier_display_name: string;
  risk_tier: string | null;
  score_band: ScoreBand | null;
  added_at: string | null;
  disclosure_status: string | null;
  undisclosed_ai: UndisclosedAiDetail | null;
  scorecard: {
    bar_name: string;
    fields_scored: number;
    fields_total: number;
    red_count: number;
    amber_count: number;
    green_count: number;
    fields: ScorecardField[];
  } | null;
  disclosure_versions: DisclosureVersion[];
  timeline: TimelineStep[];
  drift_alerts: DriftAlert[];
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SCORE_COLORS: Record<ScoreBand, string> = {
  red: 'var(--status-red)',
  amber: 'var(--status-amber)',
  green: 'var(--status-green)',
};

const SCORE_BG_COLORS: Record<ScoreBand, string> = {
  red: 'var(--status-red-bg)',
  amber: 'var(--status-amber-bg)',
  green: 'var(--status-green-bg)',
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(iso: string | null): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function OrgSupplierDetail() {
  const { orgSlug, id } = useParams<{ orgSlug: string; id: string }>();

  const detailQ = useQuery({
    queryKey: queryKeys.orgSupplierDetail(orgSlug ?? '', id ?? ''),
    queryFn: () => getOrgSupplierDetail(orgSlug!, id!),
    enabled: !!orgSlug && !!id,
  });
  const detail = {
    data: detailQ.data ?? null,
    error: detailQ.error?.message ?? null,
    loading: detailQ.isLoading,
    refetch: detailQ.refetch,
  };

  if (detail.loading) return <Spinner label="Loading supplier detail" />;
  if (detail.error) return <Alert variant="error">{detail.error}</Alert>;
  if (!detail.data) return null;

  const d = detail.data;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 960, margin: '0 auto' }}>
      {/* Back link */}
      <Link
        to={`/o/${orgSlug}/suppliers`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--accent)',
          fontSize: 'var(--font-size-2xs)',
          textDecoration: 'none',
          padding: 'var(--space-2) 0',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to portfolio
      </Link>

      {/* Supplier header */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-4) var(--space-5)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
            {/* Supplier initial avatar */}
            <div
              style={{
                background: d.score_band ? SCORE_BG_COLORS[d.score_band] : 'var(--surface-sunken)',
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-card)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  color: d.score_band ? SCORE_COLORS[d.score_band] : 'var(--text-muted)',
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {d.supplier_display_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--text)' }}>
                {d.supplier_display_name}
              </h1>
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                {d.risk_tier ? (
                  <span
                    style={{
                      background: 'var(--tier-bg, var(--surface-sunken))',
                      color: 'var(--tier-text, var(--text-muted))',
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.25rem 0.625rem',
                      borderRadius: 4,
                      fontSize: 'var(--font-size-3xs)',
                      fontWeight: 500,
                    }}
                  >
                    {d.risk_tier.charAt(0).toUpperCase() + d.risk_tier.slice(1)} risk
                  </span>
                ) : null}
                {d.score_band ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: SCORE_COLORS[d.score_band],
                        display: 'inline-block',
                      }}
                      aria-hidden="true"
                    />
                    <span style={{ color: SCORE_COLORS[d.score_band], fontSize: 'var(--font-size-2xs)', fontWeight: 500 }}>
                      {d.score_band.charAt(0).toUpperCase() + d.score_band.slice(1)} overall
                    </span>
                  </span>
                ) : null}
                {d.added_at ? (
                  <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
                    Added {formatDate(d.added_at)}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
            <button
              type="button"
              style={{
                background: 'var(--accent)',
                color: 'var(--surface)',
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '0.625rem 1rem',
                borderRadius: 8,
                fontSize: 'var(--font-size-2xs)',
                fontWeight: 500,
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              Request disclosure
            </button>
            <button
              type="button"
              style={{
                background: 'var(--surface-sunken)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '0.625rem 1rem',
                borderRadius: 8,
                fontSize: 'var(--font-size-2xs)',
                fontWeight: 500,
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Undisclosed AI banner */}
      {d.undisclosed_ai ? (
        <div
          role="alert"
          style={{
            background: 'var(--status-red-bg)',
            border: '1px solid var(--status-red)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-3) var(--space-4)',
            display: 'flex',
            gap: 'var(--space-3)',
            alignItems: 'flex-start',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
            <path d="M10 2l8 14H2l8-14z" stroke="var(--status-red)" strokeWidth="1.5" fill="none"/>
            <path d="M10 8v3M10 13.5v.5" stroke="var(--status-red)" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <div>
            <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)' }}>
              Undisclosed AI detected
            </div>
            <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 4, color: 'var(--text-muted)' }}>
              {d.undisclosed_ai.description}
            </div>
          </div>
        </div>
      ) : null}

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-4)', alignItems: 'start' }}>
        {/* Left column */}
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {/* Scorecard */}
          {d.scorecard ? (
            <ScorecardSection scorecard={d.scorecard} />
          ) : (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: 'var(--space-4)' }}>
              <EmptyState title="No scorecard" description="Scorecard data will appear once a disclosure is submitted and scored." />
            </div>
          )}

          {/* Drift alerts */}
          <DriftAlertsSection alerts={d.drift_alerts} />
        </div>

        {/* Right column */}
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {/* Disclosure versions */}
          <DisclosureVersionsSection versions={d.disclosure_versions} />

          {/* Request timeline */}
          <RequestTimelineSection steps={d.timeline} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Scorecard                                                          */
/* ------------------------------------------------------------------ */

function ScorecardSection({ scorecard }: { scorecard: NonNullable<SupplierDetailData['scorecard']> }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleCitation = useCallback((field: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  }, []);

  const total = scorecard.red_count + scorecard.amber_count + scorecard.green_count;
  const redPct = total > 0 ? (scorecard.red_count / total) * 100 : 0;
  const amberPct = total > 0 ? (scorecard.amber_count / total) * 100 : 0;
  const greenPct = total > 0 ? (scorecard.green_count / total) * 100 : 0;

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}
      aria-labelledby="scorecard-heading"
    >
      <div style={{ padding: 'var(--space-4) var(--space-4)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
          <div>
            <h2 id="scorecard-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
              Disclosure scorecard
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
              Scored against <a href="#" style={{ color: 'var(--accent)' }}>{scorecard.bar_name}</a>
            </p>
          </div>
          <span style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 500, padding: '0.25rem 0.5rem', borderRadius: 4, background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}>
            {scorecard.fields_scored} of {scorecard.fields_total} fields scored
          </span>
        </div>

        {/* Stacked bar */}
        <div style={{ marginTop: 'var(--space-3)' }}>
          <div
            role="img"
            aria-label={`Score distribution: ${scorecard.red_count} red, ${scorecard.amber_count} amber, ${scorecard.green_count} green`}
            style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}
          >
            {redPct > 0 ? <div style={{ width: `${redPct}%`, background: 'var(--status-red)' }} /> : null}
            {amberPct > 0 ? <div style={{ width: `${amberPct}%`, background: 'var(--status-amber)' }} /> : null}
            {greenPct > 0 ? <div style={{ width: `${greenPct}%`, background: 'var(--status-green)' }} /> : null}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--status-red)' }}>{scorecard.red_count} Red</span>
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--status-amber)' }}>{scorecard.amber_count} Amber</span>
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--status-green)' }}>{scorecard.green_count} Green</span>
          </div>
        </div>
      </div>

      {/* Fields table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
          <thead>
            <tr style={{ background: 'var(--surface-elevated)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-4)', fontWeight: 500, color: 'var(--text-muted)' }}>Field</th>
              <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-4)', fontWeight: 500, color: 'var(--text-muted)', width: 80 }}>Score</th>
              <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-4)', fontWeight: 500, color: 'var(--text-muted)' }}>Citation</th>
            </tr>
          </thead>
          <tbody>
            {scorecard.fields.map((f) => (
              <tr key={f.field} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 'var(--space-3) var(--space-4)', fontWeight: 500 }}>{f.field}</td>
                <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{ width: 8, height: 8, borderRadius: '50%', background: SCORE_COLORS[f.score_band] }}
                      aria-hidden="true"
                    />
                    <span style={{ color: SCORE_COLORS[f.score_band], fontWeight: 500, fontSize: 'var(--font-size-3xs)' }}>
                      {f.score_value}/{f.score_max}
                    </span>
                  </span>
                </td>
                <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                  <button
                    type="button"
                    onClick={() => toggleCitation(f.field)}
                    aria-expanded={expanded.has(f.field)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', padding: 0, font: 'inherit', color: 'inherit' }}
                  >
                    <span style={{ color: 'var(--accent)', fontSize: 'var(--font-size-3xs)', fontWeight: 500 }}>{f.citation_ref}</span>
                    <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}> &mdash; {f.citation_summary}</span>
                  </button>
                  {expanded.has(f.field) ? (
                    <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 4, color: 'var(--text-dim)' }}>
                      {f.citation_detail}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Disclosure versions                                                */
/* ------------------------------------------------------------------ */

function DisclosureVersionsSection({ versions }: { versions: DisclosureVersion[] }) {
  const [verifyOpen, setVerifyOpen] = useState<string | null>(null);

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}
      aria-labelledby="disclosure-heading"
    >
      <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)' }}>
        <h2 id="disclosure-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
          Disclosure versions
        </h2>
      </div>
      {versions.length === 0 ? (
        <div style={{ padding: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', textAlign: 'center' }}>
          No disclosures received yet.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {versions.map((v) => (
            <li key={v.version} style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                <a href="#" style={{ color: 'var(--accent)', fontSize: 'var(--font-size-2xs)', fontWeight: 500 }}>{v.version}</a>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>{formatDate(v.submitted_at)}</span>
                  {v.content_hash ? (
                    <span style={{ position: 'relative' }}>
                      <button
                        type="button"
                        aria-label={`Verify hash for ${v.version}`}
                        aria-expanded={verifyOpen === v.version}
                        onClick={() => setVerifyOpen(verifyOpen === v.version ? null : v.version)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-dim)',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          padding: 0,
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                          <path d="M7 1a6 6 0 100 12A6 6 0 007 1z" stroke="currentColor" strokeWidth="1.2"/>
                          <path d="M5 7l1.5 1.5L9 5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {verifyOpen === v.version ? (
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            zIndex: 20,
                            background: 'var(--surface-elevated)',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            padding: '0.5rem 0.75rem',
                            fontSize: 'var(--font-size-3xs)',
                            fontFamily: 'monospace',
                            color: 'var(--text-muted)',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        >
                          {v.content_hash}
                        </div>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              </div>
              <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 2, color: 'var(--text-muted)' }}>
                Source: {v.source}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Request timeline                                                   */
/* ------------------------------------------------------------------ */

function RequestTimelineSection({ steps }: { steps: TimelineStep[] }) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}
      aria-labelledby="timeline-heading"
    >
      <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)' }}>
        <h2 id="timeline-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
          Request timeline
        </h2>
      </div>
      {steps.length === 0 ? (
        <div style={{ padding: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', textAlign: 'center' }}>
          No disclosure requests yet.
        </div>
      ) : (
        <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            const dotColor =
              step.status === 'done' ? 'var(--status-green)' :
              step.status === 'active' ? 'var(--accent)' :
              'var(--border-strong)';
            const dotBg =
              step.status === 'done' ? 'var(--status-green)' :
              step.status === 'active' ? 'var(--accent)' :
              'var(--surface)';

            return (
              <div key={`${step.label}-${i}`} style={{ position: 'relative', paddingLeft: 32, minHeight: 24 }}>
                {/* Connector line */}
                {!isLast ? (
                  <div
                    style={{
                      position: 'absolute',
                      left: 7,
                      top: 24,
                      bottom: -12,
                      width: 2,
                      background: 'var(--border)',
                    }}
                  />
                ) : null}
                {/* Dot */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 4,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: `2px solid ${dotColor}`,
                    background: dotBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-hidden="true"
                >
                  {step.status === 'done' ? (
                    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                      <path d="M2 5l2 2 4-4" stroke="var(--surface)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : null}
                </div>
                <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)' }}>
                  {step.label}
                </div>
                {step.occurred_at ? (
                  <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                    {formatDate(step.occurred_at)}
                  </div>
                ) : null}
                {step.status === 'pending' && step.label.toLowerCase().includes('review') ? (
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', marginBottom: 6 }}>Awaiting your review</div>
                    <button
                      type="button"
                      style={{
                        background: 'var(--accent)',
                        color: 'var(--surface)',
                        border: 'none',
                        padding: '0.5rem 0.75rem',
                        borderRadius: 6,
                        fontSize: 'var(--font-size-3xs)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        minHeight: 36,
                      }}
                    >
                      Start review
                    </button>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                      <button
                        type="button"
                        style={{
                          background: 'var(--status-green-bg)',
                          color: 'var(--status-green)',
                          border: '1px solid var(--status-green)',
                          padding: '0.375rem 0.625rem',
                          borderRadius: 4,
                          fontSize: 'var(--font-size-3xs)',
                          fontWeight: 500,
                          cursor: 'pointer',
                          minHeight: 32,
                        }}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        style={{
                          background: 'var(--status-amber-bg)',
                          color: 'var(--status-amber)',
                          border: '1px solid var(--status-amber)',
                          padding: '0.375rem 0.625rem',
                          borderRadius: 4,
                          fontSize: 'var(--font-size-3xs)',
                          fontWeight: 500,
                          cursor: 'pointer',
                          minHeight: 32,
                        }}
                      >
                        Escalate
                      </button>
                      <button
                        type="button"
                        style={{
                          background: 'var(--status-red-bg)',
                          color: 'var(--status-red)',
                          border: '1px solid var(--status-red)',
                          padding: '0.375rem 0.625rem',
                          borderRadius: 4,
                          fontSize: 'var(--font-size-3xs)',
                          fontWeight: 500,
                          cursor: 'pointer',
                          minHeight: 32,
                        }}
                      >
                        Remediate
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Drift alerts                                                       */
/* ------------------------------------------------------------------ */

function DriftAlertsSection({ alerts }: { alerts: DriftAlert[] }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}
      aria-labelledby="drift-heading"
    >
      <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)' }}>
        <h2 id="drift-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
          Drift alerts
        </h2>
      </div>
      <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        {alerts.map((alert, i) => (
          <div
            key={`${alert.field}-${i}`}
            role="alert"
            style={{
              background: 'var(--status-amber-bg)',
              border: '1px solid var(--status-amber)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--space-3) var(--space-4)',
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'flex-start',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
              <path d="M10 2l8 14H2l8-14z" stroke="var(--status-amber)" strokeWidth="1.5" fill="none"/>
              <path d="M10 8v3M10 13.5v.5" stroke="var(--status-amber)" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <div>
              <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)' }}>
                {alert.field} score changed: {alert.old_band.charAt(0).toUpperCase() + alert.old_band.slice(1)} to {alert.new_band.charAt(0).toUpperCase() + alert.new_band.slice(1)}
              </div>
              <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 2, color: 'var(--text-muted)' }}>
                {alert.reason}
              </div>
              <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 4, color: 'var(--text-dim)' }}>
                Detected {formatDate(alert.detected_at)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
